// components/common/MultiSelect.js
import React, { useState, useRef, useEffect } from 'react';
import { getIcon } from '../../utils/icons';

/**
 * Компонент MultiSelect с поиском и автодополнением
 * @param {Object} props - Свойства компонента
 * @param {Array} props.options - Список опций для выбора
 * @param {Array} props.selectedValues - Массив выбранных значений
 * @param {Function} props.onSelectionChange - Колбэк при изменении выбора
 * @param {string} props.placeholder - Плейсхолдер для поля поиска
 * @param {string} props.searchPlaceholder - Плейсхолдер для поиска
 * @param {Function} props.onSearch - Колбэк для поиска (опционально)
 * @param {boolean} props.loading - Состояние загрузки
 * @param {string} props.displayField - Поле для отображения (по умолчанию 'name')
 * @param {string} props.valueField - Поле для значения (по умолчанию 'id')
 * @param {number} props.maxDisplayItems - Максимальное количество отображаемых выбранных элементов
 * @returns {JSX.Element} - Компонент MultiSelect
 */
const MultiSelect = ({
  options = [],
  selectedValues = [],
  onSelectionChange,
  placeholder = "Выберите элементы",
  searchPlaceholder = "Поиск...",
  onSearch,
  loading = false,
  displayField = 'name',
  valueField = 'id',
  maxDisplayItems = 3
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Фильтрация опций по поисковому запросу
  const filteredOptions = options.filter(option => {
    const searchLower = searchTerm.toLowerCase();
    const displayValue = option[displayField]?.toLowerCase() || '';
    return displayValue.includes(searchLower) && 
           !selectedValues.some(selected => selected[valueField] === option[valueField]);
  });

  // Закрытие выпадающего списка при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setFocusedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Фокус на поле поиска при открытии
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Обработка поиска с дебаунсом
  useEffect(() => {
    if (onSearch && searchTerm.length > 1) {
      const debounceTimer = setTimeout(() => {
        onSearch(searchTerm);
      }, 300);
      return () => clearTimeout(debounceTimer);
    }
  }, [searchTerm, onSearch]);

  // Обработка клавиатурных событий
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setFocusedIndex(-1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
          handleSelectOption(filteredOptions[focusedIndex]);
        }
        break;
    }
  };

  // Выбор опции
  const handleSelectOption = (option) => {
    const newSelectedValues = [...selectedValues, option];
    onSelectionChange(newSelectedValues);
    setSearchTerm('');
    setFocusedIndex(-1);
    // Оставляем выпадающий список открытым для множественного выбора
  };

  // Удаление выбранного элемента
  const handleRemoveSelected = (valueToRemove) => {
    const newSelectedValues = selectedValues.filter(
      item => item[valueField] !== valueToRemove
    );
    onSelectionChange(newSelectedValues);
  };

  // Очистка всех выбранных элементов
  const handleClearAll = () => {
    onSelectionChange([]);
  };

  // Отображение выбранных элементов
  const renderSelectedItems = () => {
    if (selectedValues.length === 0) {
      return (
        <span className="text-gray-500 text-sm">
          {placeholder}
        </span>
      );
    }

    const displayItems = selectedValues.slice(0, maxDisplayItems);
    const remainingCount = selectedValues.length - maxDisplayItems;

    return (
      <div className="flex flex-wrap gap-1">
        {displayItems.map((item, index) => (
          <span
            key={`${item[valueField]}-${index}`}
            className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full"
          >
            {item[displayField]}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveSelected(item[valueField]);
              }}
              className="ml-1 text-blue-600 hover:text-blue-800"
            >
              ×
            </button>
          </span>
        ))}
        {remainingCount > 0 && (
          <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
            +{remainingCount} еще
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Основное поле выбора */}
      <div
        className="min-h-[40px] w-full px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            {renderSelectedItems()}
          </div>
          <div className="flex items-center space-x-1 ml-2">
            {selectedValues.length > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearAll();
                }}
                className="p-1 text-gray-400 hover:text-gray-600"
                title="Очистить все"
              >
                ×
              </button>
            )}
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform ${
                isOpen ? 'transform rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Выпадающий список */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
          {/* Поле поиска */}
          <div className="p-2 border-b border-gray-200">
            <input
              ref={searchInputRef}
              type="text"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>

          {/* Список опций */}
          <div className="max-h-48 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center p-4">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
                <span className="ml-2 text-sm text-gray-600">Загрузка...</span>
              </div>
            ) : filteredOptions.length > 0 ? (
              <ul role="listbox">
                {filteredOptions.map((option, index) => (
                  <li
                    key={option[valueField]}
                    className={`px-3 py-2 cursor-pointer text-sm ${
                      index === focusedIndex
                        ? 'bg-blue-100 text-blue-900'
                        : 'hover:bg-gray-100'
                    }`}
                    onClick={() => handleSelectOption(option)}
                    role="option"
                    aria-selected={false}
                  >
                    {option[displayField]}
                    {option.description && (
                      <div className="text-xs text-gray-500 mt-1">
                        {option.description}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                {searchTerm ? 'Ничего не найдено' : 'Нет доступных опций'}
              </div>
            )}
          </div>

          {/* Информация о выборе */}
          {selectedValues.length > 0 && (
            <div className="p-2 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between text-xs text-gray-600">
                <span>Выбрано: {selectedValues.length}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearAll();
                  }}
                  className="text-blue-600 hover:text-blue-800"
                >
                  Очистить все
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;